import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaInfoCircle, FaTimes, FaLightbulb, FaChevronRight } from "react-icons/fa";
import { fetchTips, selectTipsByModule, selectTipsLoading, selectAgentEnabled } from "../agentSlice";
import { selectLogInUsername } from "../../../common/authenticateDashboard/authSlice";

export const InlineTip = ({ module }) => {
  const dispatch = useDispatch();
  const sessionUser = useSelector(selectLogInUsername);
  const clientId = sessionUser ? sessionUser.split("@")[0] : null;

  const tipsFromState = useSelector(selectTipsByModule(module));
  const loading = useSelector(selectTipsLoading(module));
  const agentEnabled = useSelector(selectAgentEnabled);

  const [isVisible, setIsVisible] = useState(true);
  const [activeTipIndex, setActiveTipIndex] = useState(0);

  useEffect(() => {
    if (clientId && module) {
      dispatch(fetchTips({ collectionName: clientId, moduleName: module }));
    }
  }, [clientId, module, dispatch]);

  // Dynamic context-aware default tips
  const getFallbackTips = () => {
    if (module === "heatmap") {
      return [
        "Homepage: 60% of users reach mid-page, only 6% reach the bottom. Consider moving important CTAs higher.",
        "Mobile Heatmaps: Drop-off rate is 15% higher than desktop above the fold. Check if elements are overlapping.",
        "Click maps reveal visitors clicking on non-interactive images. Add links to those images to improve flow."
      ];
    }
    if (module === "replay") {
      return [
        "Session replays with high 'rage click' counts indicate confusing UI elements. Filter by rage clicks to find priority fixes.",
        "Look for sessions with dead clicks where clicks have no effect, which can point to broken Javascript code.",
        "Filtering sessions by 'cart-abandonment' path helps identify exactly why checkout steps are causing drop-offs."
      ];
    }
    if (module === "nudge") {
      return [
        "Nudges targeting users with high buying intent have a 12% higher conversion rate. Setup intent-based targeting in settings.",
        "Underperforming nudges can often be fixed by simplifying CTA copy and adding a clear discount code countdown.",
        "Ensure your mobile nudge does not cover more than 30% of the screen area to satisfy Google search guidelines."
      ];
    }
    if (module === "abtest") {
      return [
        "Run A/B tests for at least 7 days to account for weekly traffic variance and ensure statistical significance.",
        "When testing hero images, focus on keeping messaging identical to isolate image impact.",
        "Avoid stopping tests early when you see initial positive changes; wait for the calculated confidence score."
      ];
    }
    return [
      "AI insights update every 24 hours based on latest session recordings and traffic parameters.",
      "Check your chatbot settings to train it with new website data sources for better conversation accuracy."
    ];
  };

  const tips = tipsFromState && tipsFromState.length > 0 ? tipsFromState : getFallbackTips();

  if (!agentEnabled || !isVisible || loading || !tips || tips.length === 0) return null;

  const currentTip = tips[activeTipIndex];

  const handleNextTip = () => {
    setActiveTipIndex((prevIndex) => (prevIndex + 1) % tips.length);
  };

  return (
    <div className="w-full mb-6">
      <div className="bg-white dark:bg-slate-900 border border-cyan-200 dark:border-indigo-900/50 border-l-4 border-l-cyan-500 dark:border-l-cyan-400 rounded-xl p-4 shadow-sm flex items-start justify-between gap-3 relative overflow-hidden">
        {/* Decorative corner glow */}
        <div className="absolute -right-8 -top-8 w-24 h-24 bg-cyan-500/10 rounded-full blur-xl pointer-events-none"></div>

        {/* Tip Content */}
        <div className="flex items-start gap-3 flex-grow min-w-0">
          <div className="p-2 rounded-lg bg-cyan-100 dark:bg-cyan-950 text-cyan-600 dark:text-cyan-400 flex items-center justify-center shrink-0 mt-0.5">
            <FaLightbulb className="text-sm animate-pulse" />
          </div>

          <div className="flex-grow min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-widest">
                AI Tip of the day
              </span>
              {tips.length > 1 && (
                <span className="text-cyan-700 dark:text-cyan-300 text-[10px] font-semibold bg-cyan-100/80 dark:bg-cyan-500/15 px-2 py-0.5 rounded-full">
                  {activeTipIndex + 1} of {tips.length}
                </span>
              )}
            </div>
            <div className="text-slate-700 dark:text-slate-200 text-xs md:text-sm font-medium leading-relaxed">
              {typeof currentTip === "string" ? (
                <p className="dark:text-slate-100" style={{ color: "#0f172a" }}>{currentTip}</p>
              ) : (
                <div className="space-y-1.5">
                  {currentTip?.title && <p className="text-sm md:text-[15px] font-bold dark:text-white leading-snug" style={{ color: "#0f172a" }}>{currentTip.title}</p>}
                  {currentTip?.recommendation && <p className="dark:text-slate-100 leading-relaxed" style={{ color: "#0f172a" }}>{currentTip.recommendation}</p>}
                  {currentTip?.evidence && <p className="text-[11px] dark:text-slate-400 italic mt-1.5 pt-1.5 border-t border-slate-100 dark:border-slate-800" style={{ color: "#334155" }}>{currentTip.evidence}</p>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Buttons / Controls */}
        <div className="flex items-center gap-1.5 shrink-0 self-center">
          {tips.length > 1 && (
            <button
              onClick={handleNextTip}
              className="text-cyan-700 dark:text-cyan-300 font-semibold text-xs bg-cyan-100/80 hover:bg-cyan-200/80 dark:bg-cyan-500/15 dark:hover:bg-cyan-500/25 px-3 py-1.5 rounded-full transition-colors flex items-center gap-1 focus:outline-none"
              title="Next tip"
            >
              Next <span>&rsaquo;</span>
            </button>
          )}

          <button
            onClick={() => setIsVisible(false)}
            className="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 text-lg font-bold ml-2 hover:bg-slate-200/60 dark:hover:bg-white/10 rounded-full w-6 h-6 flex items-center justify-center transition-colors focus:outline-none"
            title="Dismiss"
          >
            &times;
          </button>
        </div>
      </div>
    </div>
  );
};
export default InlineTip;
